import { APP_INITIALIZER, InjectionToken, ModuleWithProviders, NgModule } from '@angular/core';
import {
	MSAL_GUARD_CONFIG,
	MSAL_INSTANCE,
	MSAL_INTERCEPTOR_CONFIG,
	MsalBroadcastService,
	MsalGuard,
	MsalGuardConfiguration,
	MsalInterceptorConfiguration,
	MsalModule,
	MsalService
} from '@azure/msal-angular';
import { MsalRedirectComponent } from './msal.redirect.component';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { CustomMsalInterceptor } from './custom-msal-interceptor';
import { BrowserCacheLocation, InteractionType, IPublicClientApplication, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { BoutonSsoAzureComponent } from './bouton-sso-azure/bouton-sso-azure.component';
import { CommonModule } from '@angular/common';
import { ConfigurationSSO } from './configuration-sso.model';
import { FakePublicClientApplication } from './fake-public-client-application';
import { SsoConfigurationService } from './sso-configuration.service';

export const SSO_CONFIGURATION = new InjectionToken<SsoConfig>('config');
export interface SsoConfig {
	environnementApiUrl?: string
}

export let msalInstanceGlobal: IPublicClientApplication | null = null;

export async function MSALInstanceFactory(configurationSsoService: SsoConfigurationService): Promise<IPublicClientApplication> {
	let instance: IPublicClientApplication;

	if (!window.crypto?.subtle) {
		console.warn('Module SSO désactivé, vous n\'êtes pas dans un contexte https');
		configurationSsoService.configurationSsoCourante.actif = false;
		instance = new FakePublicClientApplication();
	}
	else {
		instance = new PublicClientApplication({
			auth: {
				clientId: configurationSsoService.configurationSsoCourante.clientId ?? '',
				redirectUri: configurationSsoService.configurationSsoCourante.redirectUri ?? '',
				authority: configurationSsoService.configurationSsoCourante.authority ?? ''
			},
			cache: {
				cacheLocation: BrowserCacheLocation.LocalStorage
			},
			system: {
				loggerOptions: {
					loggerCallback,
					logLevel: LogLevel.Warning,
					piiLoggingEnabled: false
				}
			}
		});
	}

	await instance.initialize(); // Important
	msalInstanceGlobal = instance;
	return instance;
}

function MSALInterceptorConfigFactory(configurationSsoService: SsoConfigurationService): MsalInterceptorConfiguration {
	const protectedResourceMap = new Map<string, Array<string>>();
	// L'interceptor ne doit agir que si le module SSO est actif
	if (configurationSsoService.ssoActif()) {
		protectedResourceMap.set(configurationSsoService.urlConnectionSigale, [configurationSsoService.configurationSsoCourante.protectedResource]);
	}

	return {
		interactionType: InteractionType.Redirect,
		protectedResourceMap
	};
}

function MSALGuardConfigFactory(): MsalGuardConfiguration {
	return { interactionType: InteractionType.Redirect };
}

function configurationSSOFactory(configurationSsoService: SsoConfigurationService, configurationSso: SsoConfig): () => Promise<void> {
	return async () => {
		const apiUrl = configurationSso?.environnementApiUrl ?? '';

		const configuration = await new Promise<ConfigurationSSO>((resolve, reject) => {
			configurationSsoService.chargerConfigurationOauth2(apiUrl).subscribe({
				next: resolve,
				error: reject
			});
		});

		configurationSsoService.configurationSsoCourante = configuration;
		configuration.apiUrl = apiUrl;

		// On initialise ici MSAL en une fois
		await MSALInstanceFactory(configurationSsoService);
	};
}

function loggerCallback(logLevel: LogLevel, message: string) {
	console.log(message);
}

/**
 * Module des élements communs (composants, directives, pipes...) aux applications Sigale
 */
@NgModule({
	imports: [
		CommonModule,
		MsalModule,
		BoutonSsoAzureComponent
	],
	declarations: [
		MsalRedirectComponent
	],
	exports: [
		BoutonSsoAzureComponent,
		MsalRedirectComponent
	],
	providers: [
		{
			provide: APP_INITIALIZER,
			useFactory: configurationSSOFactory,
			deps: [SsoConfigurationService, SSO_CONFIGURATION],
			multi: true
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: CustomMsalInterceptor,
			multi: true
		},
		{
			provide: MSAL_INSTANCE,
			useFactory: () => msalInstanceGlobal,
			deps: [SsoConfigurationService]
		},
		{
			provide: MSAL_GUARD_CONFIG,
			useFactory: MSALGuardConfigFactory
		},
		{
			provide: MSAL_INTERCEPTOR_CONFIG,
			useFactory: MSALInterceptorConfigFactory,
			deps: [SsoConfigurationService]
		},
		MsalService,
		MsalGuard,
		MsalBroadcastService
	]
})
export class SsoModule {
	static forRoot(config: SsoConfig): ModuleWithProviders<SsoModule> {
		return {
			ngModule: SsoModule,
			providers: [{ provide: SSO_CONFIGURATION, useValue: config }],
		};
	}
}
